import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Dashboard.css';
import './AnalyCryptoPage.css';

const CRYPTO_BASE = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8082'
  : '/mercury-crypto';

interface TradeCoinRecord {
  id: number;
  round: number;
  trade_type: string;
  code: string;
  ord_type: string;
  side: string;
  price: number | null;
  volume: number | null;
  order_uuid: string;
  state: string;
  executed_volume: number | null;
  paid_fee: number | null;
  collected_at: number;
  trade_timestamp: string;
}

interface ImportHistoryItem {
  id: number;
  startTime: string;
  endTime: string;
  label: string;
  sequenceNumber: number;
  isSelected: boolean;
}

type TradePair = { buy?: TradeCoinRecord; sell?: TradeCoinRecord };

const makePairs = (records: TradeCoinRecord[]): TradePair[] => {
  const pairs: TradePair[] = [];
  let pending: TradeCoinRecord | null = null;
  for (const r of records) {
    if (r.trade_type === 'BUY') {
      if (pending) pairs.push({ buy: pending });
      pending = r;
    } else if (r.trade_type === 'SELL') {
      pairs.push({ buy: pending ?? undefined, sell: r });
      pending = null;
    }
  }
  if (pending) pairs.push({ buy: pending });
  return pairs;
};

const profitPct = (buy?: TradeCoinRecord, sell?: TradeCoinRecord): number | null => {
  if (!buy?.price || !sell?.price || buy.price <= 0) return null;
  return ((sell.price - buy.price) / buy.price) * 100;
};

const fmtPrice = (v: number | null | undefined) =>
  v != null ? v.toLocaleString() : '-';

const fmtTime = (t?: string) =>
  t ? t.replace('T', ' ').replace('Z', '').slice(11, 19) : '-';

const fmtRate = (r: number | null) =>
  r == null ? '-' : `${r > 0 ? '+' : ''}${r.toFixed(2)}%`;

const rateColor = (r: number | null) =>
  r == null ? '#000' : r > 0 ? '#dc3545' : r < 0 ? '#0d6efd' : '#000';

const thStyle: React.CSSProperties = {
  padding: '4px 8px',
  fontWeight: 600,
  border: '1px solid #dee2e6',
  backgroundColor: '#f0f4f8',
  whiteSpace: 'nowrap',
  fontSize: 11,
  textAlign: 'center',
};
const tdStyle: React.CSSProperties = {
  padding: '4px 8px',
  border: '1px solid #dee2e6',
  fontSize: 11,
  whiteSpace: 'nowrap',
};

const AnalyCryptoPage: React.FC = () => {
  const [importHistory, setImportHistory] = useState<ImportHistoryItem[]>([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('');

  const [btRecords, setBtRecords] = useState<TradeCoinRecord[]>([]);
  const [actualRecords, setActualRecords] = useState<TradeCoinRecord[]>([]);
  const [btLoading, setBtLoading] = useState(false);
  const [actualLoading, setActualLoading] = useState(false);
  const [btError, setBtError] = useState<string | null>(null);
  const [actualError, setActualError] = useState<string | null>(null);

  const selectedItem = importHistory.find(h => h.id.toString() === selectedTimeRange);

  const filterByTime = useCallback((data: TradeCoinRecord[], start?: string, end?: string): TradeCoinRecord[] => {
    if (!start || !end) return data;
    const s = Date.parse(start.endsWith('Z') ? start : start + 'Z');
    const e = Date.parse(end.endsWith('Z') ? end : end + 'Z');
    return data.filter(r => {
      const t = Date.parse(r.trade_timestamp.replace(' ', 'T') + 'Z');
      return t >= s && t <= e;
    });
  }, []);

  const fetchBt = useCallback(async () => {
    setBtLoading(true); setBtError(null);
    try {
      const res = await fetch(`${CRYPTO_BASE}/upbit/bt-trade-history?limit=2000`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setBtRecords(j.data || []);
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Failed to fetch';
      setBtError(raw === 'Failed to fetch' ? 'mercury-crypto 서버(8082)에 연결 불가' : raw);
    } finally { setBtLoading(false); }
  }, []);

  const fetchActual = useCallback(async () => {
    setActualLoading(true); setActualError(null);
    try {
      const res = await fetch(`${CRYPTO_BASE}/upbit/orders/history?limit=1000`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setActualRecords(j.data || []);
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Failed to fetch';
      setActualError(raw === 'Failed to fetch' ? 'mercury-crypto 서버(8082)에 연결 불가' : raw);
    } finally { setActualLoading(false); }
  }, []);

  useEffect(() => { fetchBt(); fetchActual(); }, [fetchBt, fetchActual]);

  useEffect(() => {
    fetch('/mercury-backend/bt/import-history?tableName=upbit_ticker')
      .then(r => r.json())
      .then(result => {
        const h: ImportHistoryItem[] = (result.data || []).map((item: any) => ({
          id: item.id, startTime: item.start_time, endTime: item.end_time,
          label: `[${item.sequence_number}] ${item.start_time} ~ ${item.end_time}`,
          sequenceNumber: item.sequence_number || 0,
          isSelected: item.is_selected || false,
        }));
        setImportHistory(h);
        if (h.length > 0) {
          const sel = h.find(x => x.isSelected) || h[0];
          setSelectedTimeRange(sel.id.toString());
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredBt = useMemo(() =>
    filterByTime(btRecords, selectedItem?.startTime, selectedItem?.endTime),
    [btRecords, selectedItem?.startTime, selectedItem?.endTime, filterByTime]
  );

  const filteredActual = useMemo(() =>
    filterByTime(actualRecords, selectedItem?.startTime, selectedItem?.endTime),
    [actualRecords, selectedItem?.startTime, selectedItem?.endTime, filterByTime]
  );

  // round → code → records (시간순)
  const btByRound = useMemo(() => {
    const byRound = new Map<number, Map<string, TradeCoinRecord[]>>();
    for (const r of filteredBt) {
      if (!byRound.has(r.round)) byRound.set(r.round, new Map());
      const byCode = byRound.get(r.round)!;
      if (!byCode.has(r.code)) byCode.set(r.code, []);
      byCode.get(r.code)!.push(r);
    }
    byRound.forEach(byCode =>
      byCode.forEach(list => list.sort((a, b) => a.trade_timestamp.localeCompare(b.trade_timestamp)))
    );
    return byRound;
  }, [filteredBt]);

  const rounds = useMemo(() =>
    Array.from(btByRound.keys()).sort((a, b) => a - b),
    [btByRound]
  );

  // code → records (시간순)
  const actualByCode = useMemo(() => {
    const byCode = new Map<string, TradeCoinRecord[]>();
    for (const r of filteredActual) {
      if (!byCode.has(r.code)) byCode.set(r.code, []);
      byCode.get(r.code)!.push(r);
    }
    byCode.forEach(list => list.sort((a, b) => a.trade_timestamp.localeCompare(b.trade_timestamp)));
    return byCode;
  }, [filteredActual]);

  const allCodes = useMemo(() => {
    const s = new Set<string>();
    btByRound.forEach(byCode => byCode.forEach((_, c) => s.add(c)));
    actualByCode.forEach((_, c) => s.add(c));
    return Array.from(s).sort();
  }, [btByRound, actualByCode]);

  const roundTotals = useMemo(() => {
    const result = new Map<number, { sum: number; count: number }>();
    btByRound.forEach((byCode, round) => {
      let sum = 0; let count = 0;
      byCode.forEach(list =>
        makePairs(list).forEach(p => {
          const r = profitPct(p.buy, p.sell);
          if (r != null) { sum += r; count++; }
        })
      );
      result.set(round, { sum, count });
    });
    return result;
  }, [btByRound]);

  const actualTotal = useMemo(() => {
    let sum = 0; let count = 0;
    actualByCode.forEach(list =>
      makePairs(list).forEach(p => {
        const r = profitPct(p.buy, p.sell);
        if (r != null) { sum += r; count++; }
      })
    );
    return { sum, count };
  }, [actualByCode]);

  const isLoading = btLoading || actualLoading;

  return (
    <div className="dashboard-page analy-page">
      <div className="analy-scale-root">
        <div className="analy-scale-inner">
          <div className="dashboard-sections">
            <div className="dashboard-section full-width">
              <div className="section-header">
                <h2>코인 백테스트 분석</h2>
                <span style={{ marginLeft: '12px' }}>
                  <Link to="/crypto">← 크립토</Link>
                </span>
              </div>

              <div className="section-content analy-controls" style={{ width: '100%', maxWidth: 2400, margin: '0 auto' }}>
                <div style={{ width: '100%', maxWidth: 2400, margin: '0 auto', minWidth: 0 }}>

                  {/* 1. 시간 범위 선택 */}
                  <div className="analy-box" style={{ marginBottom: 20, padding: 15, backgroundColor: '#f8f9fa', borderRadius: 8, border: '1px solid #e9ecef' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#495057' }}>시간 범위 선택</h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <label style={{ color: '#6c757d', fontWeight: 500, fontSize: 13 }}>시간 범위:</label>
                      <select
                        value={selectedTimeRange}
                        onChange={e => setSelectedTimeRange(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 13, minWidth: 280 }}
                      >
                        <option value="">선택</option>
                        {importHistory.map(item => (
                          <option key={item.id} value={item.id.toString()}>{item.label}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => { fetchBt(); fetchActual(); }}
                        disabled={isLoading}
                        style={{
                          padding: '6px 12px', fontSize: 12, borderRadius: 4,
                          border: '1px solid #ced4da', cursor: isLoading ? 'not-allowed' : 'pointer',
                          backgroundColor: '#fff',
                        }}
                      >
                        {isLoading ? '로딩중...' : '새로고침'}
                      </button>
                    </div>
                  </div>

                  {/* 2. 백테 회차 vs 실제 비교 표 */}
                  <div id="print-backtest-comparison" className="analy-box" style={{ marginBottom: 20, padding: 15, backgroundColor: '#f8f9fa', borderRadius: 8, border: '1px solid #e9ecef', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                      <h4 style={{ margin: 0, color: '#495057' }}>백테 회차별 vs 실제 비교</h4>
                      {isLoading && <span style={{ fontSize: 11, color: '#6c757d' }}>데이터 로딩 중...</span>}
                    </div>

                    {(btError || actualError) && (
                      <div style={{ padding: 8, backgroundColor: '#fff5f5', color: '#dc3545', borderRadius: 4, fontSize: 12, marginBottom: 8 }}>
                        {btError || actualError}
                      </div>
                    )}

                    {/* 수익률 요약 배지 */}
                    {(rounds.length > 0 || actualTotal.count > 0) && (
                      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                        {rounds.map(round => {
                          const t = roundTotals.get(round);
                          return (
                            <div key={round} style={{ padding: '3px 10px', backgroundColor: '#fff', border: '1px solid #bee3f8', borderRadius: 12, fontSize: 11 }}>
                              <strong>회차 {round}</strong>
                              {t && t.count > 0 && (
                                <span style={{ marginLeft: 6, color: rateColor(t.sum), fontWeight: 600 }}>
                                  {fmtRate(t.sum)} ({t.count}페어)
                                </span>
                              )}
                              {t && t.count === 0 && (
                                <span style={{ marginLeft: 6, color: '#6c757d' }}>체결 없음</span>
                              )}
                            </div>
                          );
                        })}
                        {actualTotal.count > 0 && (
                          <div style={{ padding: '3px 10px', backgroundColor: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 12, fontSize: 11 }}>
                            <strong>실제</strong>
                            <span style={{ marginLeft: 6, color: rateColor(actualTotal.sum), fontWeight: 600 }}>
                              {fmtRate(actualTotal.sum)} ({actualTotal.count}페어)
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {allCodes.length === 0 && !isLoading ? (
                      <div style={{ padding: 20, textAlign: 'center', color: '#6c757d', fontSize: 13, border: '1px solid #dee2e6', borderRadius: 4 }}>
                        {btError
                          ? '서버 연결을 확인하세요.'
                          : 'bt_trade_coin에 데이터가 없습니다. go run cmd/replay/main.go 로 백테스트를 먼저 실행하세요.'}
                      </div>
                    ) : (
                      <div className="bt-table-scroll">
                        <table style={{ borderCollapse: 'collapse', fontSize: 11, tableLayout: 'auto' }}>
                          <thead>
                            <tr>
                              <th style={{ ...thStyle, minWidth: 70 }} rowSpan={2}>코인</th>
                              {rounds.map(round => (
                                <th key={round} colSpan={4} style={{ ...thStyle, backgroundColor: '#dbeafe', textAlign: 'center' }}>
                                  회차 {round}
                                  {(() => { const t = roundTotals.get(round); return t && t.count > 0 ? ` (${fmtRate(t.sum)})` : ''; })()}
                                </th>
                              ))}
                              <th colSpan={4} style={{ ...thStyle, backgroundColor: '#bfdbfe', textAlign: 'center' }}>
                                실제{actualTotal.count > 0 ? ` (${fmtRate(actualTotal.sum)})` : ''}
                              </th>
                            </tr>
                            <tr>
                              {rounds.map(round => (
                                <React.Fragment key={round}>
                                  <th style={thStyle}>매수시각</th>
                                  <th style={thStyle}>매수가</th>
                                  <th style={thStyle}>매도가</th>
                                  <th style={thStyle}>수익률</th>
                                </React.Fragment>
                              ))}
                              <th style={thStyle}>매수시각</th>
                              <th style={thStyle}>매수가</th>
                              <th style={thStyle}>매도가</th>
                              <th style={thStyle}>수익률</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allCodes.map(code => {
                              const maxPairs = Math.max(
                                ...rounds.map(r => makePairs(btByRound.get(r)?.get(code) ?? []).length),
                                makePairs(actualByCode.get(code) ?? []).length,
                                1
                              );
                              return Array.from({ length: maxPairs }, (_, pairIdx) => {
                                const isFirst = pairIdx === 0;
                                const isLast = pairIdx === maxPairs - 1;
                                return (
                                  <tr
                                    key={`${code}-${pairIdx}`}
                                    style={{
                                      borderBottom: isLast ? '2px solid #adb5bd' : '1px solid #f0f0f0',
                                      backgroundColor: isFirst ? '#fff' : '#fafafa',
                                    }}
                                  >
                                    {isFirst && (
                                      <td
                                        style={{ ...tdStyle, fontWeight: 700, verticalAlign: 'middle', textAlign: 'center' }}
                                        rowSpan={maxPairs}
                                      >
                                        {code.replace('KRW-', '')}
                                      </td>
                                    )}

                                    {/* 백테 회차별 */}
                                    {rounds.map(round => {
                                      const pairs = makePairs(btByRound.get(round)?.get(code) ?? []);
                                      const pair = pairs[pairIdx];
                                      const r = profitPct(pair?.buy, pair?.sell);
                                      return (
                                        <React.Fragment key={round}>
                                          <td style={{ ...tdStyle, color: '#6c757d' }}>
                                            {pair?.buy ? fmtTime(pair.buy.trade_timestamp) : '-'}
                                          </td>
                                          <td style={{ ...tdStyle, color: '#dc3545' }}>
                                            {pair?.buy ? fmtPrice(pair.buy.price) : '-'}
                                          </td>
                                          <td style={{ ...tdStyle, color: '#0d6efd' }}>
                                            {pair?.sell ? fmtPrice(pair.sell.price) : '-'}
                                          </td>
                                          <td style={{ ...tdStyle, color: rateColor(r), fontWeight: r != null ? 600 : 400 }}>
                                            {fmtRate(r)}
                                          </td>
                                        </React.Fragment>
                                      );
                                    })}

                                    {/* 실제 체결 */}
                                    {(() => {
                                      const pairs = makePairs(actualByCode.get(code) ?? []);
                                      const pair = pairs[pairIdx];
                                      const r = profitPct(pair?.buy, pair?.sell);
                                      return (
                                        <>
                                          <td style={{ ...tdStyle, color: '#6c757d', backgroundColor: '#f0f8ff' }}>
                                            {pair?.buy ? fmtTime(pair.buy.trade_timestamp) : '-'}
                                          </td>
                                          <td style={{ ...tdStyle, color: '#dc3545', backgroundColor: '#f0f8ff' }}>
                                            {pair?.buy ? fmtPrice(pair.buy.price) : '-'}
                                          </td>
                                          <td style={{ ...tdStyle, color: '#0d6efd', backgroundColor: '#f0f8ff' }}>
                                            {pair?.sell ? fmtPrice(pair.sell.price) : '-'}
                                          </td>
                                          <td style={{ ...tdStyle, color: rateColor(r), fontWeight: r != null ? 600 : 400, backgroundColor: '#f0f8ff' }}>
                                            {fmtRate(r)}
                                          </td>
                                        </>
                                      );
                                    })()}
                                  </tr>
                                );
                              });
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* 3. Ticker/Trade/Orderbook 상세 분석 안내 */}
                  <div className="analy-box" style={{ padding: 12, backgroundColor: '#fff8e1', borderRadius: 8, border: '1px solid #ffe082', fontSize: 12, color: '#795548' }}>
                    <strong>Ticker / Trade / Orderbook 상세 분석 (예정)</strong>
                    <div style={{ marginTop: 6, lineHeight: 1.6 }}>
                      현재 임포트된 데이터: <code>bt_upbit_ticker</code>(MongoDB), <code>bt_upbit_trade</code>(MongoDB), <code>bt_upbit_orderbook</code>(MongoDB)<br />
                      코인별 매수 시점 전후 Ticker 가격 추이, Trade 체결 흐름, Orderbook 잔량 비율을 차트로 분석하려면
                      mercury-backend에 조회 API 엔드포인트 추가가 필요합니다.
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyCryptoPage;
